import {
  AllocationPaymentTargetAccount,
  BackendError,
  BackendErrorList,
  CashActionResponseMessage,
  CashActionResponseMessageType,
  DepositPaymentTargetAccount,
  isAxiosError,
  isDefined,
  Keys,
  Loadable,
  PaymentResource,
  ResponseMessageType,
  TransferPaymentItem,
  TransferPaymentTargetAccount,
  usePaymentCommon,
  UsePaymentCommonProps,
} from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useLocale } from '../../../context/NbLocalizationContext';
import { FeedbackBoxProps } from '../../../shared/components/feedback-box/FeedbackBox';
import { useResource, useResourceHeaders } from '../../../shared/services/useResource';
import {
  CashActionStep,
  cashActionStepAtom,
  cashActionTempAtom,
  getTargetAccountAborterAtom,
  receivedMessagesAtom,
  smsCodeErrorAtom,
  tempIdAtom,
} from '../states/cashActionAtoms';
import { targetAccountsAtom } from '../states/depositAtoms';

type GetSubmitErrorsReturnType = Required<Pick<FeedbackBoxProps, 'type' | 'details' | 'title'>>;

export type CashActionSourceAccountsUnion = string | TransferPaymentItem;

export type CashActionTargetAccountsUnion =
  | DepositPaymentTargetAccount
  | TransferPaymentTargetAccount
  | AllocationPaymentTargetAccount;

export const usePayment = () => {
  const paymentResource = useResource(PaymentResource);
  const { getResourceHeaderWithToken } = useResourceHeaders();
  const [smsCodeError, setSmsCodeError] = useAtom(smsCodeErrorAtom);
  const [targetAccounts, setTargetAccounts] = useAtom(targetAccountsAtom);
  const [cashActionStep, setCashActionStep] = useAtom(cashActionStepAtom);
  const [cashActionTemp, setCashActionTemp] = useAtom(cashActionTempAtom);
  const [receivedMessages, setReceivedMessages] = useAtom(receivedMessagesAtom);
  const [getTargetAccountAborter, setGetTargetAccountAborter] = useAtom(getTargetAccountAborterAtom);

  const [tempId, setTempId] = useAtom(tempIdAtom);
  const { translation } = useLocale();

  /**
   * Handle and show backend messages.
   * @param messages Array of message objects
   * @returns props for 'FeedbackBox' if any message is present, undefined otherwise
   */
  const getSubmitErrors = useCallback(
    (messages: Array<CashActionResponseMessage>): GetSubmitErrorsReturnType | undefined => {
      const messagesMappedInit: Record<CashActionResponseMessageType | 'undefined', Array<string>> = {
        confirm: [],
        error: [],
        info: [],
        warn: [],
        undefined: [],
      };

      const messagesMapped = messages.reduce(
        (prev, cur) => ({ ...prev, [cur.msgType ?? 'undefined']: [...prev[cur.msgType ?? 'undefined'], cur.msg] }),
        messagesMappedInit,
      );

      if (messagesMapped.error.length > 0) {
        return {
          title: translation.general.attention,
          type: 'error',
          details: messagesMapped.error,
        };
      }

      if (messagesMapped.warn.length > 0) {
        return {
          title: translation.general.attention,
          type: 'warning',
          details: messagesMapped.warn,
        };
      }

      if (messagesMapped.confirm.length > 0) {
        return {
          title: translation.general.attention,
          type: 'confirm',
          details: messagesMapped.confirm,
        };
      }

      if (messagesMapped.info.length > 0) {
        return {
          title: translation.general.attention,
          type: 'info',
          details: messagesMapped.info,
        };
      }

      if (messagesMapped.undefined.length) {
        console.error(`Unknown backend messages received! ${messagesMapped.undefined.toString()}`);
        return {
          title: translation.general.attention,
          type: 'error',
          details: [translation.general.error.serverError],
        };
      }

      return undefined;
    },
    [translation],
  );

  const handleTwoFactorRestart = useCallback(async () => {
    setTempId(undefined);
    setReceivedMessages([]);
    setSmsCodeError(undefined);
    setCashActionStep(CashActionStep.Params);
  }, [setCashActionStep, setReceivedMessages, setSmsCodeError, setTempId]);

  const handleTwoFactorError = useCallback(
    async (err: any) => {
      if (!isAxiosError(err)) {
        setCashActionStep(CashActionStep.ReceivedMessages);
        setReceivedMessages([
          { msgType: ResponseMessageType.Error, msg: translation.cashAction.errorsCommon.cannotSubmit },
        ]);
        return;
      }

      const data = err?.response?.data as BackendErrorList;

      if (isAxiosError(err) && isDefined(data?.errors?.length) && data.errors.length > 0) {
        const smsCodeError = data.errors.find(
          error =>
            error.errorCode === Keys.ErrorCode.twoFactor.invalidSmsCode ||
            error.errorCode === Keys.ErrorCode.twoFactor.orderSmsConfirmationError ||
            error.errorCode === Keys.ErrorCode.twoFactor.expiredSmsCode,
        );
        if (smsCodeError) {
          setSmsCodeError(smsCodeError);
        } else {
          setCashActionStep(CashActionStep.ReceivedMessages);
          setReceivedMessages([
            { msgType: ResponseMessageType.Error, msg: translation.cashAction.errorsCommon.cannotSubmit },
          ]);
        }
      } else {
        setCashActionStep(CashActionStep.ReceivedMessages);
        setReceivedMessages([
          { msgType: ResponseMessageType.Error, msg: translation.cashAction.errorsCommon.cannotSubmit },
        ]);
      }
    },
    [setCashActionStep, setReceivedMessages, setSmsCodeError, translation],
  );

  const handleCashActionDialogOpen = useCallback((cashActionCb: () => Promise<void>, cashCloseCb: () => void) => {
    cashActionCb();
    cashCloseCb();
  }, []);

  const canRestartTwoFactor = useCallback((error: BackendError): boolean => {
    return error.errorCode !== Keys.ErrorCode.confirmTwoFactor.INVALID_SMS_CODE;
  }, []);

  const p: UsePaymentCommonProps = useMemo(
    () => ({
      paymentResource,
      getResourceHeaderWithToken,
    }),
    [getResourceHeaderWithToken, paymentResource],
  );

  const clearPaymentAtomState = useCallback(() => {
    if (isDefined(getTargetAccountAborter)) {
      getTargetAccountAborter.abort();
      setGetTargetAccountAborter(undefined);
    }

    setTargetAccounts(Loadable.uninitialized);
    setCashActionStep(CashActionStep.Params);
    setCashActionTemp(null);
    setTempId(undefined);
    setSmsCodeError(undefined);
    setReceivedMessages([]);
  }, [
    getTargetAccountAborter,
    setTargetAccounts,
    setCashActionStep,
    setCashActionTemp,
    setTempId,
    setSmsCodeError,
    setReceivedMessages,
    setGetTargetAccountAborter,
  ]);

  return {
    ...usePaymentCommon(p),
    smsCodeError,
    setSmsCodeError,
    tempId,
    setTempId,
    handleTwoFactorError,
    canRestartTwoFactor,
    cashActionStep,
    setCashActionStep,
    cashActionTemp,
    setCashActionTemp,
    targetAccounts,
    setTargetAccounts,
    getSubmitErrors,
    clearPaymentAtomState,
    receivedMessages,
    setReceivedMessages,
    handleTwoFactorRestart,
    handleCashActionDialogOpen,
  };
};
